// tinyid — a short, URL-safe, crypto-random identifier (nanoid semantics, no
// dependency). Used to give equipment INSTANCES a stable per-piece id at edit time,
// so two pieces of the same catalog slug (two pistols, two armor plates) can be told
// apart and play-state can key to a specific piece. Uniqueness only has to hold
// WITHIN one character's blob, so 12 chars of this alphabet is astronomically safe.
//
//   import { tinyid } from "../lib/tinyid"
//   tinyid()      // => "q7f3ka9m2xhd"  (12 chars)
//   tinyid(8)     // => "k3f9x2ab"       (custom length)

// URL-safe, no look-alike ambiguity concerns here — the full alphanumeric-lowercase
// set. 36 symbols. (Kept lowercase so ids are case-insensitively distinct and tidy in
// JSON / query strings.)
const ALPHABET = "0123456789abcdefghijklmnopqrstuvwxyz"

// Bias-free selection: a naive `byte % 36` skews toward the low symbols (256 isn't a
// multiple of 36, so bytes 0..35 occur once more than 36..255 wrap around). We instead
// mask each byte down to the fewest bits that cover the alphabet, then REJECT any value
// that lands outside it and redraw. Every symbol is then exactly equiprobable.
//
//   36 symbols -> need 6 bits (2^6 = 64 >= 36); mask = 63. A drawn 0..63 that is >= 36
//   is rejected. This is exactly nanoid's algorithm.
const MASK = 63 // (1 << 6) - 1, the smallest mask covering ALPHABET.length (36)

// crypto.getRandomValues refuses a single request over 65,536 bytes, so cap each draw
// well under that. For any real id length one draw suffices; the cap only matters for
// absurdly large `size` (which then just loops).
const MAX_DRAW = 65536

// Bounds: below 4 an id is collision-prone even per-character; above 32 is pointless
// bloat for a per-blob-unique key. A caller passing outside this is a bug — fail loud.
const MIN_SIZE = 4
const MAX_SIZE = 32

// A crypto-random id of `size` chars (default 12), uniform over ALPHABET. Draws bytes
// from the CSPRNG and rejection-samples; the batch is oversized so the common case
// fills in one draw even with some rejections.
export function tinyid(size = 12) {
  if (!Number.isInteger(size) || size < MIN_SIZE || size > MAX_SIZE) {
    throw new RangeError(`tinyid size must be an integer in [${MIN_SIZE}, ${MAX_SIZE}], got ${size}`)
  }
  let id = ""
  // Oversample: at 36/64 acceptance, ~1.78 bytes per accepted char. Draw generously so
  // one getRandomValues call almost always suffices; loop covers the rare shortfall.
  const step = Math.min(MAX_DRAW, Math.ceil((size * 64) / ALPHABET.length))
  while (id.length < size) {
    const bytes = crypto.getRandomValues(new Uint8Array(step))
    for (let i = 0; i < bytes.length && id.length < size; i++) {
      const n = bytes[i] & MASK
      if (n < ALPHABET.length) id += ALPHABET[n]
    }
  }
  return id
}
